import { createColumnHelper } from '@tanstack/react-table';
import _ from 'lodash';

import { isoDateFromTimestamp } from '@/portainer/filters/filters';

import { Badge } from '@@/Badge';
import { Tooltip } from '@@/Tip/Tooltip';
import { filterHOC } from '@@/datatables/Filter';
import { multiple } from '@@/datatables/filter-types';

import { ScopedKubeconfigRow } from '../types';

/** Namespaces past this collapse into a +N, so one wide grant can't stretch the row. */
const visibleNamespaceCount = 2;

const secretsIncluded = 'Included';
const secretsExcluded = 'Excluded';
const statusActive = 'Active';
const statusExpired = 'Expired';

const columnHelper = createColumnHelper<ScopedKubeconfigRow>();

const description = columnHelper.accessor('description', {
  header: 'Description',
  cell: ({ getValue }) => getValue() || <Unset />,
});

const owner = columnHelper.accessor((row) => row.username ?? '', {
  id: 'username',
  header: 'Owner',
  cell: ({ getValue }) => getValue() || <Unset />,
  enableColumnFilter: true,
  filterFn: multiple,
  meta: { filter: filterHOC('Filter by owner') },
});

const namespaces = columnHelper.accessor((row) => row.namespaces ?? [], {
  id: 'namespaces',
  header: 'Namespaces',
  enableSorting: false,
  cell: ({ getValue }) => <NamespaceList namespaces={getValue()} />,
});

// A label rather than the boolean, so the filter menu reads as words.
const secrets = columnHelper.accessor(
  (row) => (row.includeSecrets ? secretsIncluded : secretsExcluded),
  {
    id: 'includeSecrets',
    header: 'Secrets',
    cell: ({ getValue }) =>
      getValue() === secretsIncluded ? (
        <Badge type="warnSecondary">{secretsIncluded}</Badge>
      ) : (
        <Unset />
      ),
    enableColumnFilter: true,
    filterFn: multiple,
    meta: { filter: filterHOC('Filter by secrets') },
  }
);

const created = columnHelper.accessor('createdAt', {
  header: 'Created',
  cell: ({ getValue }) => <Timestamp value={getValue()} />,
});

const expires = columnHelper.accessor('expiresAt', {
  header: 'Expires',
  cell: ({ getValue }) => <Timestamp value={getValue()} />,
});

const status = columnHelper.accessor(
  (row) => (row.active ? statusActive : statusExpired),
  {
    id: 'active',
    header: 'Status',
    cell: ({ getValue }) =>
      getValue() === statusActive ? (
        <Badge type="success">{statusActive}</Badge>
      ) : (
        <Badge type="danger">{statusExpired}</Badge>
      ),
    enableColumnFilter: true,
    filterFn: multiple,
    meta: { filter: filterHOC('Filter by status') },
  }
);

const environment = columnHelper.accessor('environmentName', {
  id: 'endpointId',
  header: 'Environment',
  cell: ({ getValue }) => getValue() || <Unset />,
  enableColumnFilter: true,
  filterFn: multiple,
  meta: { filter: filterHOC('Filter by environment') },
});

/**
 * Owner only means something to someone who can see other people's rows; for
 * everyone else it repeats their own name down the whole table.
 */
export function buildColumns({
  showOwner,
  showEnvironment,
}: {
  showOwner: boolean;
  showEnvironment: boolean;
}) {
  return _.compact([
    description,
    showOwner && owner,
    showEnvironment && environment,
    namespaces,
    secrets,
    created,
    expires,
    status,
  ]);
}

function Unset() {
  return <span className="text-muted">-</span>;
}

function Timestamp({ value }: { value?: number }) {
  // Records issued before createdAt existed store a zero, which is not 1970.
  if (!value) {
    return <Unset />;
  }

  return <>{isoDateFromTimestamp(value)}</>;
}

function NamespaceList({ namespaces }: { namespaces: Array<string> }) {
  if (namespaces.length === 0) {
    return <Unset />;
  }

  const shown = namespaces.slice(0, visibleNamespaceCount);
  const hidden = namespaces.slice(visibleNamespaceCount);

  return (
    <span className="flex items-center gap-1">
      {shown.join(', ')}
      {hidden.length > 0 && (
        <>
          <span className="text-muted">+{hidden.length}</span>
          <Tooltip message={hidden.join(', ')} />
        </>
      )}
    </span>
  );
}
